// src/utils/apiError.jsx
// Gestion d'erreurs centralisée + toasts modernes.
// Couvre : validations express-validator (errors[]), { message }/{ error },
// erreurs réseau/CORS/timeout, et codes HTTP génériques.
import React from "react";
import toast from "react-hot-toast";

/**
 * Extrait une liste de messages lisibles depuis une erreur axios/fetch.
 * @returns {string[]} toujours au moins 1 message
 */
export function getApiErrorMessages(
  error,
  fallback = "Une erreur est survenue. Veuillez réessayer."
) {
  const data = error?.response?.data;

  // 1) express-validator : { errors: [...] } ou { details: [...] }
  const list = Array.isArray(data?.errors)
    ? data.errors
    : Array.isArray(data?.details)
    ? data.details
    : null;
  if (list && list.length) {
    const msgs = list
      .map((e) => e?.msg || e?.message || (typeof e === "string" ? e : ""))
      .filter(Boolean);
    if (msgs.length) return msgs;
  }

  // 2) erreurs mongoose : { errors: { champ: { message } } }
  if (data?.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
    const msgs = Object.values(data.errors)
      .map((e) => e?.message || e?.msg)
      .filter(Boolean);
    if (msgs.length) return msgs;
  }

  // 3) message unique
  const single = data?.message || data?.error || data?.msg;
  if (single) return [String(single)];

  // 4) pas de réponse serveur (réseau / CORS / timeout)
  if (error?.request && !error?.response) {
    if (error?.code === "ECONNABORTED")
      return ["La requête a expiré. Vérifiez votre connexion et réessayez."];
    return ["Impossible de joindre le serveur. Vérifiez votre connexion internet."];
  }

  // 5) code HTTP générique
  const status = error?.response?.status;
  if (status) {
    const map = {
      400: "Données invalides. Vérifiez le formulaire.",
      401: "Vous devez vous connecter pour continuer.",
      403: "Action non autorisée.",
      404: "Ressource introuvable.",
      409: "Conflit : cette opération existe déjà.",
      413: "Fichier trop volumineux.",
      422: "Données invalides. Vérifiez le formulaire.",
      429: "Trop de tentatives. Réessayez dans un instant.",
      500: "Erreur serveur. Réessayez plus tard.",
      502: "Service momentanément indisponible.",
      503: "Service momentanément indisponible.",
    };
    return [map[status] || `Erreur ${status}.`];
  }

  return [error?.message || fallback];
}

/**
 * Affiche un toast d'erreur moderne (liste si plusieurs erreurs de validation).
 * @returns {string[]} les messages affichés
 */
export function notifyApiError(error, fallback, options = {}) {
  const messages = getApiErrorMessages(error, fallback);
  const { id, title = "Action impossible" } = options;

  if (messages.length === 1) {
    toast.error(messages[0], { id, duration: 5000 });
    return messages;
  }

  toast.error(
    (t) => (
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", maxWidth: 360 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, marginBottom: 6, color: "#b91c1c" }}>
            {title} ({messages.length})
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.45 }}>
            {messages.map((m, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {m}
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          aria-label="Fermer"
          style={{
            border: 0,
            background: "transparent",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            color: "#9ca3af",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    ),
    { id, duration: 7000 }
  );

  return messages;
}

/**
 * Toast de succès cohérent avec le reste de l'app.
 */
export function notifySuccess(message, options = {}) {
  return toast.success(message, { duration: 3500, ...options });
}
